import Stepper from 'ext-ui/components/Stepper/Stepper'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedCreate/CardFeedCreate.style'
import StepContactBank from 'src/app/CardFeedCreate/StepContactBank'
import StepEnterFeedDetails from 'src/app/CardFeedCreate/StepEnterFeedDetails'
import StepReviewTerms from 'src/app/CardFeedCreate/StepReviewTerms'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import Box from 'src/components/Box/Box'
import { useCurrentBrand } from 'src/context/ConfigContext/useConfig'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { cardFeedDetailsUrl, homeUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { CreateCardFeedRequest } from 'src/queries/cardFeeds/cardFeeds.models'
import { useCreateCardFeed } from 'src/queries/cardFeeds/domain/cardFeeds.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'

export type WizardStep = 1 | 2 | 3

// `location.state` is untrusted runtime input — a stale history entry or a
// hand-crafted navigation can carry any `initialStep`. Clamp it to a real
// wizard step so the content always renders; anything outside 2–3 falls back
// to step 1.
export const toValidInitialStep = (value: unknown): WizardStep =>
  value === 2 || value === 3 ? value : 1

// Narrows the plain number the Stepper emits back to the strict WizardStep
// union before it reaches the wizard's navigation handler.
export const isWizardStep = (value: number): value is WizardStep =>
  value === 1 || value === 2 || value === 3

// Stepper labels, ordered by step. Indices map 1:1 to WizardStep (step N is
// STEP_TITLE_KEYS[N - 1]). Translated at render time before being handed to the
// brand-agnostic ext-ui Stepper.
const STEP_TITLE_KEYS = [
  'Contact Bank',
  'Enter Feed Details',
  'Review Enrollment Terms',
] as const

export const NETWORK_VALUES = ['mastercard', 'visa'] as const
export type CardNetwork = (typeof NETWORK_VALUES)[number]

export type FieldKey =
  | 'deliveryId'
  | 'regionId'
  | 'processorId'
  | 'issuerId'
  | 'companyId'

type FieldDefBase = {
  key: FieldKey
  labelKey: string
  placeholderKey: string
  testId: string
  tooltipKey?: string
}

// A format constraint and its error message travel together: a `pattern`
// requires an `errorKey` (and vice versa), so a field can never fail validation
// without an inline message to surface. Enforced at compile time via the union.
export type FieldDef = FieldDefBase &
  (
    | { pattern?: undefined; errorKey?: undefined }
    | { pattern: RegExp; errorKey: string }
  )

export const NETWORK_FIELDS: Record<CardNetwork, readonly FieldDef[]> = {
  mastercard: [
    {
      key: 'deliveryId',
      labelKey: 'Delivery ID',
      placeholderKey: 'Enter Delivery ID',
      testId: 'card-feed-create-delivery-id',
      tooltipKey:
        'The Delivery ID is a unique identifier your bank provides to route transactions to Extend.',
    },
  ],
  visa: [
    {
      key: 'regionId',
      labelKey: 'Region ID',
      placeholderKey: 'Enter Region ID',
      testId: 'card-feed-create-region-id',
      pattern: /^\d+$/,
      errorKey: 'Region ID must be a number',
    },
    {
      key: 'processorId',
      labelKey: 'Processor ID',
      placeholderKey: 'Enter Processor ID',
      testId: 'card-feed-create-processor-id',
      pattern: /^\d+$/,
      errorKey: 'Processor ID must be a number',
    },
    {
      key: 'issuerId',
      labelKey: 'Issuer ID',
      placeholderKey: 'Enter Issuer ID',
      testId: 'card-feed-create-issuer-id',
      pattern: /^\d+$/,
      errorKey: 'Issuer ID must be a number',
    },
    {
      key: 'companyId',
      labelKey: 'Company ID',
      placeholderKey: 'Enter Company ID',
      testId: 'card-feed-create-company-id',
      pattern: /^\d+$/,
      errorKey: 'Company ID must be a number',
    },
  ],
}

export type FeedFields = Partial<Record<FieldKey, string>>

// Returns the field's error key when a non-empty value violates its format
// constraint. An empty value returns undefined here — emptiness is a "required"
// concern handled by isFeedDetailsValid, not a format error to surface inline.
export const getFieldError = (
  def: FieldDef,
  value: string | undefined,
): string | undefined => {
  const trimmed = (value ?? '').trim()
  if (trimmed.length === 0) return undefined
  if (def.pattern && !def.pattern.test(trimmed)) return def.errorKey
  return undefined
}

export const isFeedDetailsValid = (
  network: CardNetwork | undefined,
  fields: FeedFields,
): boolean => {
  if (network === undefined) return false
  return NETWORK_FIELDS[network].every((def) => {
    const trimmed = (fields[def.key] ?? '').trim()
    if (trimmed.length === 0) return false
    return !(def.pattern && !def.pattern.test(trimmed))
  })
}

export const buildCreateCardFeedRequest = (
  network: CardNetwork,
  fields: FeedFields,
): CreateCardFeedRequest => {
  if (network === 'mastercard') {
    return {
      cardNetwork: 'MASTERCARD',
      // Format validation/normalization is owned by the backend.
      deliveryId: (fields.deliveryId ?? '').trim(),
    }
  }
  return {
    cardNetwork: 'VISA',
    regionId: fields.regionId ?? '',
    processorId: fields.processorId ?? '',
    issuerId: fields.issuerId ?? '',
    companyId: fields.companyId ?? '',
  }
}

const CardFeedCreate: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const history = useHistory()
  const location = useLocation<{ initialStep?: WizardStep }>()
  const dispatch = useDispatch()
  const { APP_NAME: rawAppName } = useCurrentBrand()

  const { data: featuresData, isLoading: isFeaturesLoading } =
    useQueryUserFeatures([UserFeature.BULK_CREDIT_CARD_ENROLLMENT])
  const bulkCreditCardEnrollmentEnabled =
    featuresData?.BULK_CREDIT_CARD_ENROLLMENT ?? false
  const { enable_card_feeds: cardFeedsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}
  const currentOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const isAllowed = bulkCreditCardEnrollmentEnabled && !!cardFeedsEnabled

  const [step, setStep] = useState<WizardStep>(
    toValidInitialStep(location.state?.initialStep),
  )
  const [network, setNetwork] = useState<CardNetwork | undefined>()
  const [fields, setFields] = useState<FeedFields>({})
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>()

  const { mutate: createCardFeed, isPending: isSubmitting } = useCreateCardFeed(
    {
      onSuccess: (data) => {
        analytics.track('card feeds create', {
          action: 'submit success',
          network: data.cardNetwork,
          id: data.id,
        })
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <p>
                {translate(
                  'Card feed created — your bank will activate it shortly.',
                )}
              </p>
            ),
          }),
        )
        history.push(cardFeedDetailsUrl(data.id))
      },
      onError: (error) => {
        const message =
          error.response?.data?.error ??
          translate('Something went wrong. Please try again.')
        setSubmitError(message)
        analytics.track('card feeds create', {
          action: 'submit failed',
          network,
        })
      },
    },
  )

  // Same defensive gate as CardFeeds list page: a direct URL must redirect
  // home when either flag is off, but wait for the feature query to settle.
  useEffect(() => {
    if (isFeaturesLoading) return
    if (!isAllowed) {
      history.replace(homeUrl())
    }
  }, [isAllowed, isFeaturesLoading, history])

  useEffect(() => {
    if (!isAllowed) return
    analytics.page(AnalyticsPageName.OrgSettingsCardFeedsCreate)
  }, [analytics, isAllowed])

  useEffect(() => {
    if (!isAllowed) return
    analytics.track('card feeds create - step viewed', { step })
  }, [analytics, isAllowed, step])

  if (!isAllowed) {
    return null
  }

  const handleNetworkChange = (next: CardNetwork | undefined): void => {
    if (next !== network) {
      setTermsAccepted(false)
      setSubmitError(undefined)
      setFields({})
    }
    setNetwork(next)
  }

  // Centralised backward navigation. Returning to Contact Bank (step 1)
  // discards the feed details entirely — the user re-selects the network and
  // re-enters the IDs on step 2, so nothing should be retained.
  const goToStep = (target: WizardStep): void => {
    setSubmitError(undefined)
    if (target === 1) {
      setNetwork(undefined)
      setFields({})
      setTermsAccepted(false)
    }
    setStep(target)
  }

  // Stepper only surfaces completed steps as tappable, but guard against
  // forward jumps anyway.
  const handleStepSelect = (target: WizardStep): void => {
    if (target >= step) return
    analytics.track('card feeds create', {
      action: 'stepper step selected',
      step: target,
    })
    goToStep(target)
  }

  const handleConfirm = (): void => {
    if (network === undefined || !currentOrganization?.id) return
    setSubmitError(undefined)
    createCardFeed({
      organizationId: currentOrganization.id,
      body: buildCreateCardFeedRequest(network, fields),
    })
  }

  return (
    <>
      <Helmet>
        <title>{translate('Card Feeds')}</title>
      </Helmet>
      <LayoutPage
        title={<span>{translate('Card Feeds')}</span>}
        subtitle={
          <span css={style.subtitle}>
            {translate(
              'Connect your bank to bring existing cards into ${appName} and manage spend in one place.',
              { appName: translate(rawAppName) },
            )}
          </span>
        }
      >
        <div css={style.wizardLayout}>
          <Box noPadding outlineBorder css={style.stepperCard}>
            <Stepper
              steps={STEP_TITLE_KEYS.map((key, index) => ({
                title: translate(key),
                testId: `card-feed-create-step-${index + 1}`,
              }))}
              currentStep={step}
              onStepSelect={(target): void => {
                if (isWizardStep(target)) handleStepSelect(target)
              }}
              heading={translate('Get Started')}
              ariaLabel={translate('Card Feeds enrollment progress')}
              getStepNavLabel={(stepName): string =>
                translate('Go back to ${stepName}', { stepName })
              }
              testId="card-feed-create-stepper"
            />
          </Box>
          <Box noPadding outlineBorder css={style.contentCard}>
            {step === 1 && <StepContactBank onNext={(): void => setStep(2)} />}
            {step === 2 && (
              <StepEnterFeedDetails
                network={network}
                fields={fields}
                onNetworkChange={handleNetworkChange}
                onFieldsChange={setFields}
                onBack={(): void => goToStep(1)}
                onNext={(): void => setStep(3)}
              />
            )}
            {step === 3 && (
              <StepReviewTerms
                network={network}
                termsAccepted={termsAccepted}
                onTermsAcceptedChange={setTermsAccepted}
                onBack={(): void => goToStep(2)}
                onConfirm={handleConfirm}
                isSubmitting={isSubmitting}
                submitError={submitError}
              />
            )}
          </Box>
        </div>
      </LayoutPage>
    </>
  )
}

export default CardFeedCreate
